import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { clsx } from 'clsx';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  ScaleIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  PaperClipIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

// Navigation items with required permissions
const navigation = [
  { name: 'nav.dashboard', href: '/dashboard', icon: HomeIcon, permission: 'dashboard.view' },
  { name: 'nav.cases', href: '/cases', icon: BriefcaseIcon, permission: 'cases.view' },
  { name: 'nav.foncier', href: '/foncier', icon: BuildingOfficeIcon, permission: 'foncier.view' },
  { name: 'nav.contentieux', href: '/contentieux', icon: ScaleIcon, permission: 'contentieux.view' },
  { name: 'nav.assurances', href: '/assurances', icon: ShieldCheckIcon, permission: 'assurances.view' },
  { name: 'nav.contrats', href: '/contrats', icon: DocumentTextIcon, permission: 'contrats.view' },
  { name: 'nav.documents', href: '/documents', icon: PaperClipIcon, permission: 'documents.view' },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { hasPermission, canAccessAdmin } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const isRTL = i18n.language === 'ar';

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter(item => 
    hasPermission(item.permission)
  );

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'ar' : 'fr';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const handleNavigation = (href) => {
    navigate(href);
    setSidebarOpen(false);
  };

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const SidebarContent = () => (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center justify-center border-b border-gray-100">
        <img
          className="h-12 w-auto"
          src={require('../assets/logo.jpg')}
          alt={t('app.title')}
        />
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-1">
          {filteredNavigation.map((item) => (
            <li key={item.name}>
              <button
                onClick={() => handleNavigation(item.href)}
                className={clsx(
                  'group flex w-full gap-x-3 rounded-md p-2 text-sm font-medium leading-6',
                  isActive(item.href)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                )}
              >
                <item.icon
                  className={clsx(
                    'h-6 w-6 shrink-0',
                    isActive(item.href)
                      ? 'text-primary-600'
                      : 'text-gray-400 group-hover:text-primary-600'
                  )}
                />
                {t(item.name)}
              </button>
            </li>
          ))}

          {/* Admin Settings Link */}
          {canAccessAdmin() && (
            <li className="mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => handleNavigation('/admin/settings')}
                className={clsx(
                  'group flex w-full gap-x-3 rounded-md p-2 text-sm font-medium leading-6',
                  location.pathname.startsWith('/admin')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                )}
              >
                <Cog6ToothIcon
                  className={clsx(
                    'h-6 w-6 shrink-0',
                    location.pathname.startsWith('/admin')
                      ? 'text-primary-600'
                      : 'text-gray-400 group-hover:text-primary-600'
                  )}
                />
                {t('nav.settings')}
              </button>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-gray-900/80"
            onClick={() => setSidebarOpen(false)}
          />
          <div
            className={clsx(
              'absolute inset-y-0 w-full max-w-xs bg-white shadow-xl',
              isRTL ? 'right-0' : 'left-0'
            )}
          >
            <div
              className={clsx(
                'absolute top-0 flex w-16 justify-center pt-5',
                isRTL ? 'left-full' : 'right-full'
              )}
            >
              <button
                type="button"
                className="-m-2.5 p-2.5"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">{t('common.closeSidebar')}</span>
                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Static sidebar for desktop */}
      <div
        className={clsx(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col',
          isRTL ? 'lg:right-0' : 'lg:left-0'
        )}
      >
        <div
          className={clsx(
            'flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4',
            isRTL ? 'border-l border-gray-200' : 'border-r border-gray-200'
          )}
        >
          <SidebarContent />
        </div>
      </div>

      {/* Main content area */}
      <div className={clsx(isRTL ? 'lg:pr-72' : 'lg:pl-72')}>
        {/* Top navigation bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          {/* Mobile menu button */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">{t('common.openSidebar')}</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Separator */}
          <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            {/* Spacer */}
            <div className="flex-1" />

            {/* Right side items */}
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Global search */}
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = event.currentTarget;
                  const input = form.querySelector('input[name="q"]');
                  if (input?.value) {
                    navigate(`/search?q=${encodeURIComponent(input.value)}`);
                  }
                }}
                className="hidden md:flex items-center gap-2"
              >
                <input
                  name="q"
                  type="search"
                  placeholder={t('search.placeholder', 'Search...')}
                  className="w-56 rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <button
                  type="submit"
                  className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                >
                  {t('search.search', 'Search')}
                </button>
              </form>

              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
              >
                {i18n.language === 'fr' ? 'العربية' : 'Français'}
              </button>

              {/* Separator */}
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" aria-hidden="true" />

              {/* User menu */}
              <div className="flex items-center gap-x-3">
                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center gap-x-2 hover:bg-gray-100 rounded-md p-1 transition-colors"
                  title={t('profile.title', 'Profile')}
                >
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  <span className="hidden lg:flex lg:items-center">
                    <span className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </span>
                  </span>
                </button>
              </div>

              {/* Logout button */}
              <button
                onClick={logout}
                className="flex items-center gap-x-2 text-sm text-gray-600 hover:text-gray-900"
                title={t('nav.logout')}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="hidden sm:inline">{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
